import { useState, useEffect, useCallback } from 'react';
import { FluentProvider, webLightTheme, webDarkTheme } from '@fluentui/react-components';
import { MsalProvider, AuthenticatedTemplate, UnauthenticatedTemplate, useMsal } from '@azure/msal-react';
import { PublicClientApplication } from '@azure/msal-browser';
import { msalConfig, loginRequest } from './auth/msalConfig';
import { DashboardPage } from './pages/DashboardPage';
import { ProjectsPage } from './pages/ProjectsPage';
import { ProjectDetailPage } from './pages/ProjectDetailPage';
import { DocumentViewerPage } from './pages/DocumentViewerPage';
import { AssessmentChatPage } from './pages/AssessmentChatPage';
import { ChatPage } from './pages/ChatPage';
import { SimulationResultsPage } from './pages/SimulationResultsPage';
import { RegistersPage } from './pages/RegistersPage';
import { CostEstimatePage } from './pages/CostEstimatePage';
import { useApiToken } from './hooks/useApiToken';
import { listProjects, createProject, scaffoldDocuments } from './services/project-service';
import { listDocuments, type MethodologyDocument } from './services/document-service';
import { Button, Text, Tab, TabList, Spinner } from '@fluentui/react-components';
import { CloudRegular, FolderRegular, SignOutRegular, BeakerRegular, ChatRegular, WeatherMoonRegular, WeatherSunnyRegular, SettingsRegular, ClipboardRegular } from '@fluentui/react-icons';
import type { Project } from './services/project-service';
import './App.css';

const msalInstance = new PublicClientApplication(msalConfig);

function LoginPage() {
  const { instance } = useMsal();

  return (
    <div className="login-page">
      <Text size={600} weight="semibold">Cloud Adoption Agent</Text>
      <Text size={300}>Sign in to manage Azure migration projects</Text>
      <Button
        appearance="primary"
        size="large"
        onClick={() => instance.loginPopup(loginRequest)}
      >
        Sign in with Microsoft
      </Button>
    </div>
  );
}

function AppShell({ darkMode, setDarkMode }: { darkMode: boolean; setDarkMode: (v: boolean) => void }) {
  const { instance, accounts } = useMsal();
  const getToken = useApiToken();
  const [activeTab, setActiveTab] = useState('dashboard');
  const [loading, setLoading] = useState(true);
  const [projects, setProjects] = useState<Project[]>([]);
  const [allDocs, setAllDocs] = useState<MethodologyDocument[]>([]);
  const [selectedProject, setSelectedProject] = useState<Project | null>(null);
  const [projectDocs, setProjectDocs] = useState<MethodologyDocument[]>([]);
  const [selectedDoc, setSelectedDoc] = useState<MethodologyDocument | null>(null);

  const userName = accounts[0]?.name ?? 'User';

  const fetchProjects = useCallback(async (customerId: string): Promise<Project[]> => {
    try {
      const token = await getToken();
      return await listProjects(customerId, token);
    } catch {
      return [];
    }
  }, [getToken]);

  const refreshAllProjects = useCallback(async () => {
    const customers = ['gms', 'heneways-freight', 'kzn-health', 'colourfield', 'recharge-rental'];
    const allProjects: Project[] = [];
    const allDocuments: MethodologyDocument[] = [];
    const token = await getToken();

    for (const cid of customers) {
      const data = await fetchProjects(cid);
      allProjects.push(...data);
      // Fetch docs for each project
      for (const p of data) {
        if (p.id) {
          try {
            const docs = await listDocuments(cid, p.id, token);
            allDocuments.push(...docs);
          } catch { /* skip */ }
        }
      }
    }
    setProjects(allProjects);
    setAllDocs(allDocuments);
    setLoading(false);
  }, [fetchProjects, getToken]);

  // Load projects on mount
  useEffect(() => {
    refreshAllProjects();
  }, [refreshAllProjects]);

  const handleCreateProject = async (project: Omit<Project, 'id'>) => {
    try {
      const token = await getToken();
      const created = await createProject(project as Project, token);

      // Scaffold all methodology documents
      if (created.id) {
        await scaffoldDocuments(created.id, created.customerId, token);
      }

      // Reload projects list
      await refreshAllProjects();
      setActiveTab('projects');
    } catch (err) {
      console.error('Failed to create project:', err);
    }
  };

  const handleSelectProject = async (project: Project) => {
    setSelectedProject(project);
    setSelectedDoc(null);
    setActiveTab('project-detail');
    try {
      const token = await getToken();
      const docs = await listDocuments(project.customerId, project.id!, token);
      setProjectDocs(docs);
    } catch (err) {
      console.error('Failed to load documents:', err);
      setProjectDocs([]);
    }
  };

  const handleStartAssessment = () => {
    setActiveTab('assessment-chat');
  };

  const handleSelectDocument = (doc: MethodologyDocument) => {
    setSelectedDoc(doc);
    setActiveTab('document-viewer');
  };

  const handleBackToProject = () => {
    setSelectedDoc(null);
    setActiveTab('project-detail');
  };

  const handleBackToProjects = () => {
    setSelectedProject(null);
    setProjectDocs([]);
    setActiveTab('projects');
  };

  const handleOpenRegisters = () => {
    setActiveTab('registers');
  };

  const handleOpenCostEstimate = () => {
    setActiveTab('cost-estimate');
  };

  return (
    <div className="app-shell">
      <div className="app-sidebar">
        <div className="sidebar-header">
          <CloudRegular style={{ fontSize: 20 }} />
          <Text size={400} weight="semibold">Cloud Adoption</Text>
        </div>
        <TabList
          vertical
          selectedValue={activeTab}
          onTabSelect={(_e, data) => setActiveTab(data.value as string)}
        >
          <Tab value="dashboard" icon={<CloudRegular />}>Dashboard</Tab>
          <Tab value="projects" icon={<FolderRegular />}>Projects</Tab>
          <Tab value="chat" icon={<ChatRegular />}>Chat</Tab>
          <Tab value="registers" icon={<ClipboardRegular />}>Registers</Tab>
          <Tab value="lab" icon={<BeakerRegular />}>Quality Lab</Tab>
          <Tab value="settings" icon={<SettingsRegular />}>Settings</Tab>
        </TabList>
        <div className="sidebar-footer">
          <Text size={200} className="text-muted">{userName}</Text>
          <div className="sidebar-footer-actions">
            <Button
              appearance="subtle"
              size="small"
              icon={darkMode ? <WeatherSunnyRegular /> : <WeatherMoonRegular />}
              onClick={() => setDarkMode(!darkMode)}
              title={darkMode ? 'Light mode' : 'Dark mode'}
            />
            <Button
              appearance="subtle"
              size="small"
              icon={<SignOutRegular />}
              onClick={() => instance.logoutPopup()}
              title="Sign out"
            />
          </div>
        </div>
      </div>
      <div className="app-content">
        {loading && <div className="loading-container"><Spinner size="large" label="Loading projects..." /></div>}
        {!loading && activeTab === 'dashboard' && <DashboardPage projects={projects} documentCounts={{
          total: allDocs.length,
          draft: allDocs.filter(d => d.status === 'draft').length,
          review: allDocs.filter(d => d.status === 'review').length,
          final: allDocs.filter(d => d.status === 'final').length,
        }} onSelectProject={handleSelectProject} />}
        {!loading && activeTab === 'projects' && (
          <ProjectsPage
            projects={projects}
            onCreateProject={handleCreateProject}
            onSelectProject={handleSelectProject}
          />
        )}
        {activeTab === 'project-detail' && selectedProject && (
          <ProjectDetailPage
            project={selectedProject}
            documents={projectDocs}
            onBack={handleBackToProjects}
            onSelectDocument={handleSelectDocument}
            onStartAssessment={handleStartAssessment}
            onOpenRegisters={handleOpenRegisters}
            onOpenCostEstimate={handleOpenCostEstimate}
          />
        )}
        {activeTab === 'document-viewer' && selectedDoc && (
          <DocumentViewerPage
            document={selectedDoc}
            onBack={handleBackToProject}
          />
        )}
        {activeTab === 'assessment-chat' && selectedProject && (
          <AssessmentChatPage
            project={selectedProject}
            onBack={handleBackToProject}
          />
        )}
        {activeTab === 'registers' && selectedProject && (
          <RegistersPage project={selectedProject} onBack={handleBackToProject} />
        )}
        {activeTab === 'registers' && !selectedProject && (
          <div style={{ padding: 24 }}><Text className="text-muted">Select a project first to view registers.</Text></div>
        )}
        {activeTab === 'cost-estimate' && selectedProject && (
          <CostEstimatePage project={selectedProject} onBack={handleBackToProject} />
        )}
        {!loading && activeTab === 'chat' && <ChatPage project={selectedProject} />}
        {!loading && activeTab === 'lab' && <SimulationResultsPage />}
      </div>
    </div>
  );
}

function AppContent({ darkMode, setDarkMode }: { darkMode: boolean; setDarkMode: (v: boolean) => void }) {
  return (
    <>
      <AuthenticatedTemplate>
        <AppShell darkMode={darkMode} setDarkMode={setDarkMode} />
      </AuthenticatedTemplate>
      <UnauthenticatedTemplate>
        <LoginPage />
      </UnauthenticatedTemplate>
    </>
  );
}

export default function App() {
  const [darkMode, setDarkMode] = useState(() =>
    typeof window !== 'undefined' && localStorage.getItem('theme') === 'dark'
  );

  return (
    <MsalProvider instance={msalInstance}>
      <FluentProvider theme={darkMode ? webDarkTheme : webLightTheme}>
        <AppContent darkMode={darkMode} setDarkMode={(v: boolean) => { setDarkMode(v); localStorage.setItem('theme', v ? 'dark' : 'light'); }} />
      </FluentProvider>
    </MsalProvider>
  );
}
